import { useState, useEffect, useCallback, useRef } from 'react';
import { Claim, ClaimStatus } from '@/lib/types';
import { generateClaim } from '@/lib/mockData';

const STATUS_PROGRESSION: ClaimStatus[] = [
  'note_signed',
  'coding', 
  'denial_check',
  'auto_fix',
  'submitted',
  'pending',
  'paid',
];

export function useClaimsStream(initialClaims: Claim[] = [], isPaused: boolean = false) {
  const [claims, setClaims] = useState<Claim[]>(() => {
    if (initialClaims.length > 0) return initialClaims;
    // Generate initial set of claims
    return Array.from({ length: 25 }, () => generateClaim());
  });
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const addNewClaim = useCallback(() => {
    const newClaim = generateClaim({ status: 'note_signed' });
    setClaims(prev => [newClaim, ...prev.slice(0, 49)]);
  }, []);

  const progressClaim = useCallback((claimId: string) => {
    setClaims(prev => prev.map(claim => {
      if (claim.id !== claimId) return claim;
      
      const currentIndex = STATUS_PROGRESSION.indexOf(claim.status);
      if (currentIndex === -1 || currentIndex >= STATUS_PROGRESSION.length - 1) {
        return claim;
      }
      
      // Sometimes claims get denied instead of paid
      const nextStatus = currentIndex === STATUS_PROGRESSION.length - 2 && Math.random() > 0.92
        ? 'denied' as ClaimStatus
        : STATUS_PROGRESSION[currentIndex + 1];
      
      return {
        ...claim,
        status: nextStatus,
        submittedAt: nextStatus === 'submitted' ? new Date() : claim.submittedAt,
        paidAt: nextStatus === 'paid' ? new Date() : claim.paidAt,
      };
    }));
  }, []);

  useEffect(() => {
    if (isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Add new claims periodically
    const newClaimInterval = setInterval(() => {
      if (Math.random() > 0.4) {
        addNewClaim();
      }
    }, 3000);

    // Progress existing claims
    const progressInterval = setInterval(() => {
      setClaims(prev => {
        const inProgressClaims = prev.filter(c => 
          STATUS_PROGRESSION.includes(c.status) && 
          c.status !== 'paid' && 
          c.status !== 'denied'
        );
        
        if (inProgressClaims.length > 0) {
          const claimToProgress = inProgressClaims[Math.floor(Math.random() * inProgressClaims.length)];
          const currentIndex = STATUS_PROGRESSION.indexOf(claimToProgress.status);
          
          if (currentIndex >= 0 && currentIndex < STATUS_PROGRESSION.length - 1) {
            const nextStatus = currentIndex === STATUS_PROGRESSION.length - 2 && Math.random() > 0.92
              ? 'denied' as ClaimStatus
              : STATUS_PROGRESSION[currentIndex + 1];
            
            return prev.map(c => 
              c.id === claimToProgress.id 
                ? {
                    ...c,
                    status: nextStatus,
                    submittedAt: nextStatus === 'submitted' ? new Date() : c.submittedAt,
                    paidAt: nextStatus === 'paid' ? new Date() : c.paidAt,
                  }
                : c
            );
          }
        }
        return prev;
      });
    }, 2000);

    intervalRef.current = progressInterval;

    return () => {
      clearInterval(newClaimInterval);
      clearInterval(progressInterval);
    };
  }, [isPaused, addNewClaim]);

  return {
    claims,
    addNewClaim,
    progressClaim,
  };
}
